/**
 * components/client/WelcomeScreen.tsx
 *
 * Welcome screen displayed before the client starts filling the form.
 * Shows the professional's logo (if set), form title, custom message,
 * and a prominent CTA to begin. Includes an animated entry.
 *
 * Updated June 2026: Vertically centered card (items-center on all screens).
 *   Reassurance messages enlarged to text-sm text-neutral-500 with w-4 h-4 icons
 *   for better readability on mobile. Spacing adjusted (mt-6 / mt-3).
 */

import { useState } from 'react';
import { ArrowRight, Lock, Cloud } from 'lucide-react';
import ClientBrandingHeader from '../branding/ClientBrandingHeader';

interface WelcomeScreenProps {
  title: string;
  customMessage: string | null;
  logoUrl: string | null;
  senderDisplayName: string | null;
  onStart: () => void;
}

export default function WelcomeScreen({
  title,
  customMessage,
  logoUrl,
  onStart,
}: WelcomeScreenProps) {
  const [isStarting, setIsStarting] = useState(false);

  const handleStart = () => {
    if (isStarting) return;
    setIsStarting(true);
    onStart();
  };

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-xl animate-welcome-enter">
        <div className="bg-white rounded-3xl shadow-soft-xl p-7 md:p-12 text-center">
          {logoUrl && (
            <div className="flex justify-center mb-6">
              <ClientBrandingHeader
                logoUrl={logoUrl}
                displayName={null}
                size="lg"
              />
            </div>
          )}

          <h1 className="text-2xl md:text-3xl font-serif font-medium text-neutral-900 mb-5 leading-tight tracking-tight">
            {title}
          </h1>

          {customMessage && (
            <p className="text-base md:text-lg text-neutral-600 leading-relaxed mb-7">
              {customMessage}
            </p>
          )}

          <button
            onClick={handleStart}
            disabled={isStarting}
            className="group w-full px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 active:scale-[0.98] transition-all duration-150 shadow-soft flex items-center justify-center gap-3 disabled:opacity-75 disabled:cursor-not-allowed touch-manipulation"
          >
            Commencer
            <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </button>

          <div className="flex items-center justify-center gap-2 mt-6">
            <Cloud className="w-4 h-4 text-neutral-400 flex-shrink-0" />
            <p className="text-sm text-neutral-500">
              Vos réponses sont sauvegardées automatiquement · Vous pouvez reprendre à tout moment
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mt-3">
            <Lock className="w-4 h-4 text-neutral-400 flex-shrink-0" />
            <p className="text-sm text-neutral-500">
              Connexion chiffrée · Vos réponses sont transmises en toute sécurité
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
